import { Identificacao } from "./components/identificacao/Identificacao";
import { Especialidade } from "./components/especialidade/Especialidade";
import { Usuario } from "./components/usuario/Usuario";
import { Processo } from "./components/processo/Processo";
import { Retroatividade } from "./components/retroatividade/Retroatividade";
import { Simulacao } from "./components/simulacao/Simulacao";
import { Contrato } from "./components/contrato/Contrato";
import FormContext from "../../store/form-context";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import MultiStepsContext from "../../store/multi-steps-form-context";
import { ProgressBar } from "./components/progress-bar/ProgressBar";
import { Cpf } from "./components/cpf/Cpf";
import { RetroatividadeRenovacao } from "./components/retroatividade-renovacao/RetroatividadeRenovacao";
import { Pagamento } from "./components/pagamento/Pagamento";
import { Apolice } from "./components/apolice/Apolice";
import ApiCrosier from "../../services/api-crosier";
import { FullscreenLoader } from "../UI/fullscreen-loader/FullscreenLoader";
import { Alert } from "../UI/alert/Alert";
import { isEmpty } from "lodash";

const INITIAL_STEPS = [
  <Identificacao />,
  <Usuario />,
  <Especialidade />,
  <Processo />,
];

const MAIN_FLOW_STEPS = [
  <Retroatividade />,
  <Cpf />,
  <Simulacao />,
  <Contrato />,
  <Pagamento />,
];

const CIRURGIAO_FLOW_STEPS = [
  <Cpf />,
  <Simulacao />,
  <Contrato />,
  <Pagamento />,
];

const RENOVACAO_FLOW_STEPS = [
  <RetroatividadeRenovacao />,
  <Apolice />,
  <Cpf />,
  <Simulacao />,
];

export const Form = () => {
  const { categoria, renovacao, cotacaoUuid, updateState } =
    useContext(FormContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const steps = useMemo(() => {
    if (!isEmpty(cotacaoUuid)) return [<Pagamento />];

    const isCirurgiao = categoria === "PLASTIC-SURGERY";
    const isRenovacao = renovacao === "renovacao";
    let stepList;

    if (isRenovacao) {
      stepList = RENOVACAO_FLOW_STEPS;
    } else {
      stepList = isCirurgiao ? CIRURGIAO_FLOW_STEPS : MAIN_FLOW_STEPS;
    }

    return INITIAL_STEPS.concat(stepList);
  }, [categoria, renovacao, cotacaoUuid]);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep + 1 === steps.length;

  const component = useMemo(() => steps[currentStep], [steps, currentStep]);

  const goForward = useCallback(() => {
    if (isLastStep) return;

    setCurrentStep((previous) => ++previous);
    window.scroll(0, 0);
  }, [isLastStep]);

  const goBack = useCallback(() => {
    if (isFirstStep) return;

    setCurrentStep((previous) => --previous);
    window.scroll(0, 0);
  }, [isFirstStep]);

  const goToStep = useCallback((newStep: number) => {
    setCurrentStep(newStep);
  }, []);

  const contextValue = useMemo(() => {
    return {
      currentStep,
      isFirstStep,
      isLastStep,
      goForward,
      goBack,
      goToStep,
    };
  }, [currentStep, isFirstStep, isLastStep, goForward, goBack, goToStep]);

  useEffect(() => {
    const initializeTrafficSource = () => {
      const trafficSource = document.cookie
        .split("; ")
        .find((cookie) => cookie.startsWith("__trf.src"))
        ?.replace("__trf.src=", "");

      if (trafficSource) {
        updateState({ trafficSource });
      }
    };

    initializeTrafficSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialize = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const ref = urlParams.get("ref");
      const cotacaoUuid = urlParams.get("cotacao");
      const urlTrafficSource = urlParams.get("utm_source");
      const urlTrafficMedium = urlParams.get("utm_medium");
      const urlTrafficCampaign = urlParams.get("utm_campaign");
      const documentReferrer = document.referrer;

      const trafficUpdates: Record<string, string> = {};
      if (urlTrafficSource) trafficUpdates.urlTrafficSource = urlTrafficSource;
      if (urlTrafficMedium) trafficUpdates.urlTrafficMedium = urlTrafficMedium;
      if (urlTrafficCampaign) trafficUpdates.urlTrafficCampaign = urlTrafficCampaign;
      if (documentReferrer) trafficUpdates.documentReferrer = documentReferrer;

      if (Object.keys(trafficUpdates).length > 0 || ref) {
        updateState({ 
          ...trafficUpdates,
          ...(ref && { parceiro: ref }) 
        });
      }

      if (cotacaoUuid) {
        try {
          setIsLoading(true);
          const crosierService = new ApiCrosier();
          const response = await crosierService.fetchCotacao(cotacaoUuid);
          updateState({
            ...response.formData,
            cotacaoUuid,
            simulacaoData: response.cotacao,
            cotacaoData: response.cotacao,
          });
        } catch (error: any) {
          console.warn(error);
          setShowError(true);
        } finally {
          window.scroll(0, 0);
          setIsLoading(false);
        }
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FullscreenLoader isLoading={isLoading} />
      <Alert
        message="Não foi possível carregar a cotação. Por favor, tente novamente."
        showAlert={showError}
        onClose={() => setShowError(false)}
      />
      <MultiStepsContext.Provider value={contextValue}>
        {<ProgressBar step={currentStep} />}
        <form onSubmit={(event) => event.preventDefault()}>
          <div>{component}</div>
        </form>
      </MultiStepsContext.Provider>
    </>
  );
};
